import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
}

export const HojaPasePIS: React.FC<Props> = ({ expediente }) => {
  const cita = expediente.documentos.pasePIS.codigoCita || 'PIS-4694507'
  const fechaCita = expediente.documentos.pasePIS.fechaCita || '04/10/2026'

  return (
    <div className="relative w-full bg-[#fdfdf9] text-slate-900 shadow-2xl rounded-sm p-6 sm:p-10 font-sans border border-slate-300 print:border-none print:shadow-none print:p-4 text-[10px] sm:text-[11px] leading-tight select-none">
      {/* Marca de agua SEMAR / ASIPONA */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] overflow-hidden">
        <div className="text-[100px] font-black uppercase text-emerald-950 -rotate-30 select-none tracking-widest whitespace-nowrap">
          PIS ASIPONA SEMAR
        </div>
      </div>

      {/* Encabezado Oficial Institucional SEMAR / ASIPONA Manzanillo */}
      <div className="border-b-2 border-emerald-900 pb-3 mb-3 flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded bg-emerald-900 text-white font-black text-xs flex items-center justify-center font-serif">
              ⚓
            </div>
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest text-emerald-900">
                GOBIERNO DE MÉXICO · SECRETARÍA DE MARINA (SEMAR)
              </p>
              <h2 className="text-sm sm:text-base font-black tracking-tight text-slate-950 uppercase">
                ASIPONA MANZANILLO · PUERTO INTELIGENTE SEGURO (PIS)
              </h2>
              <p className="text-[8px] font-mono text-slate-500">
                PASE OFICIAL DE ACCESO VEHICULAR Y CONTROL DE GARITA FISCALIZADA
              </p>
            </div>
          </div>
        </div>

        <div className="text-right">
          <span className="font-mono text-xs font-black text-emerald-900 bg-emerald-50 border border-emerald-300 px-2.5 py-1 rounded inline-block">
            FOLIO CITA: {cita}
          </span>
          <p className="font-mono text-[8px] text-slate-600 mt-1">VENTANA: 14:00 - 16:00 HRS</p>
          <p className="font-mono text-[8px] text-emerald-700 font-bold">ESTATUS: CITA ACTIVA Y VALIDADA</p>
        </div>
      </div>

      {/* Código de Barras Superior para Escaneo en Garita */}
      <div className="border border-slate-300 bg-white p-2 mb-3 rounded flex flex-col items-center justify-center">
        <div className="h-10 w-72 flex items-center justify-between px-2 bg-white">
          {Array.from({ length: 58 }).map((_, i) => (
            <div
              key={i}
              className={`h-full ${i % 4 === 0 ? 'w-1 bg-black' : i % 7 === 0 ? 'w-1.5 bg-black' : 'w-0.5 bg-black'}`}
            />
          ))}
        </div>
        <span className="font-mono text-xs font-bold text-slate-800 tracking-widest mt-1">
          *{cita.replace(/[^A-Z0-9]/g, '')}*
        </span>
        <span className="text-[7.5px] font-mono text-slate-400">
          CÓDIGO BIDIMENSIONAL PARA LECTURA EN BARRERA AUTOMATIZADA DE ACCESO NORTE
        </span>
      </div>

      {/* Grid Principal de Datos de Acceso */}
      <div className="border-2 border-slate-900 divide-y-2 divide-slate-900 mb-3">
        {/* Fila 1: Terminal, Buque y Operación */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 bg-slate-100/70 font-mono text-[9px]">
          <div className="col-span-4 p-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">TERMINAL DE DESTINO:</span>
            <span className="font-black text-xs text-slate-950 uppercase">{expediente.buque.terminal}</span>
            <span className="block text-[7.5px] text-slate-600">MUELLE 10-11 RECINTO FISCALIZADO</span>
          </div>

          <div className="col-span-4 p-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">TIPO MOVIMIENTO PIS:</span>
            <span className="font-bold text-xs text-blue-900">
              {expediente.tipoOperacion === 'EXPORTACION' ? 'INGRESO LLENO EXPORTACIÓN' : 'RETIRO IMPORTACIÓN'}
            </span>
            <span className="block text-[7.5px] text-slate-600">ORDEN DE SERVICIO: B48 AUTORIZADA</span>
          </div>

          <div className="col-span-4 p-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">FECHA PROGRAMADA:</span>
            <span className="font-bold text-xs text-slate-900">{fechaCita}</span>
            <span className="block text-[7.5px] text-emerald-700 font-bold">CARRIL ASIGNADO: C-04</span>
          </div>
        </div>

        {/* Fila 2: Transportista y Conductor */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 p-2 font-mono text-[9px] bg-white">
          <div className="col-span-6 pr-2 space-y-1">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">EMPRESA DE TRANSPORTE Y CAAT:</span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.transportista.razonSocial}</p>
            <p className="text-[8px] text-slate-700">
              CAAT: <strong className="text-slate-900">{expediente.actores.transportista.caat}</strong> · REGISTRO PIS
              VIGENTE
            </p>
          </div>

          <div className="col-span-6 pl-2 space-y-1">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">DATOS DEL OPERADOR:</span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.transportista.chofer}</p>
            <p className="text-[8px] text-slate-700">
              LICENCIA FEDERAL: <strong className="text-slate-900">B-88219033</strong> · APTO MÉDICO VIGENTE
            </p>
          </div>
        </div>

        {/* Fila 3: Vehículo y Carga */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 p-2 font-mono text-[9px] bg-slate-50/70">
          <div className="col-span-4 pr-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">UNIDAD DE CARGA:</span>
            <p className="font-bold text-xs text-slate-900">PLACAS: {expediente.actores.transportista.placas}</p>
            <p className="text-[8px] text-slate-500">TRACTOCAMIÓN T3-S2 · DOBLE ARTICULADO</p>
          </div>

          <div className="col-span-4 px-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">CONTENEDOR ASOCIADO:</span>
            <p className="font-black text-xs text-blue-950">{expediente.contenedor.numero}</p>
            <p className="text-[8px] text-slate-500">
              TIPO: {expediente.contenedor.tipo} · {expediente.contenedor.lineaNaviera}
            </p>
          </div>

          <div className="col-span-4 pl-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">PRECINTOS FISCALES:</span>
            <p className="font-bold text-slate-900 text-[8.5px]">{expediente.contenedor.sellos}</p>
            <p className="text-[8px] text-emerald-700 font-bold">PRE-REGISTRO ANEXO 29 VÁLIDO</p>
          </div>
        </div>
      </div>

      {/* Servicios Autorizados en Garita */}
      <div className="border border-slate-900 p-2 mb-3 bg-white font-mono text-[9px] flex items-center justify-between">
        <div className="space-y-0.5">
          <span className="font-sans font-bold uppercase text-[7.5px] text-slate-500 block">SERVICIOS DE TERMINAL SOLICITADOS:</span>
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[8px]">
              ✓ BP1 - Báscula Pesaje Oficial SOLAS
            </span>
            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-bold text-[8px]">
              ✓ B48 - Pase Terminal SSA Manzanillo
            </span>
            {expediente.contenedor.tipo === '40RF' && (
              <span className="px-2 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold text-[8px]">
                ✓ Conexión Reefers Patio Refrigerado
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <span className="text-[7.5px] font-sans font-bold text-slate-500 block">VGM DECLARADO:</span>
          <span className="font-black text-sm text-slate-950">
            {expediente.contenedor.vgmDeclaradoKg.toLocaleString()} KG
          </span>
        </div>
      </div>

      {/* Código QR de Garita PIS y Firma de Autorización */}
      <div className="border-2 border-slate-900 p-2 bg-slate-50 font-mono text-[8px] relative">
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-3 flex flex-col items-center justify-center p-1 bg-white border border-slate-300 rounded">
            <QRCodeSVG value={`https://pis.puertosmexico.gob.mx/cita/valida?cita=${cita}&placas=${expediente.actores.transportista.placas}&cntr=${expediente.contenedor.numero}`} size={72} />
            <span className="text-[6.5px] font-sans font-bold text-slate-600 mt-1">TOKEN QR ASIPONA</span>
          </div>

          <div className="col-span-5 space-y-1 text-slate-600 font-sans">
            <p className="font-bold text-slate-900">
              INSTRUCCIONES DE ACCESO AL RECINTO FISCALIZADO:
            </p>
            <ul className="list-disc pl-3 text-[7.5px] space-y-0.5">
              <li>Presentar este código QR legible al escáner óptico del carril 04.</li>
              <li>Velocidad máxima en garita y báscula: 10 km/h.</li>
              <li>El operador debe portar chaleco reflejante, casco y calzado industrial con casquillo.</li>
            </ul>
          </div>

          <div className="col-span-4 border-l border-slate-300 pl-3 text-center">
            <div className="h-9 flex items-center justify-center">
              <span className="font-serif italic text-base text-emerald-950 font-bold tracking-tight">
                Capitán M. Aréchiga
              </span>
            </div>
            <div className="border-t border-slate-800 pt-0.5">
              <span className="font-sans font-bold text-[7.5px] uppercase text-slate-800 block">
                SEGURIDAD Y PROTECCIÓN PORTUARIA (SEMAR)
              </span>
              <span className="text-[6.5px] text-slate-500 font-mono">ASIPONA PUERTO DE MANZANILLO</span>
            </div>
          </div>
        </div>

        {/* Sello de Goma Redondo ASIPONA */}
        <div className="absolute right-36 top-[-30px] pointer-events-none transform -rotate-12">
          <div className="h-20 w-20 rounded-full border-2 border-emerald-900 bg-emerald-50/80 backdrop-blur-sm p-1 flex flex-col items-center justify-center text-center text-emerald-950 shadow-md">
            <span className="text-[5.5px] font-black uppercase tracking-wider">ASIPONA MANZANILLO</span>
            <span className="text-[7.5px] font-black uppercase text-emerald-900">ACCESO AUTORIZADO</span>
            <span className="text-[5.5px] font-mono">SEMAR / PIS</span>
          </div>
        </div>
      </div>

      <div className="mt-2 text-center text-[7px] font-mono text-slate-400">
        PASE ELECTRÓNICO EXPEDIDO POR LA ADMINISTRACIÓN DEL SISTEMA PORTUARIO NACIONAL (ASIPONA MANZANILLO).
      </div>
    </div>
  )
}
